import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
/**
 * Editorial Authoring V2 — MACRO_SENSORY authoring (BUILD 6).
 *
 * Rendered by EditorialSpreadWorkspaceV2.jsx in place of the generic
 * workspace body when block.master_type === "MACRO_SENSORY".
 * Self-contained: owns its own modal chrome, save flow and save-state
 * display. No recognition/parser step, no AI, no schema editor.
 *
 * No import from @/components/editorial/ (the legacy authoring UI, or
 * MacroSensoryMaster.jsx specifically — read only as the confirmed
 * source of truth, never imported, never modified). Allowed imports:
 * @/lib/* (editorialImageStyle, hasExplicitEditorialCrop, both
 * imported directly and used exactly as the real Reader does rather
 * than reimplemented; resolveMediaUrl / mediaRecordUrl for resolving a
 * picked/uploaded media record to a URL), and this app's own
 * @/components/editorial-v2/* siblings (EditorialMediaPickerV2,
 * EditorialCropV2).
 *
 * ── STORAGE CONTRACT (confirmed) ──────────────────────────────────────
 *   content.macro_sensory = {
 *     eyebrow, title, subtitle, text_blocks, sensory_notes,
 *     accent_phrase, caption, main_image, mobile_image
 *   }
 *   text_blocks:    { id, text }[]
 *   sensory_notes:  { id, label, text }[] — max 6
 *   each image:     { media_id, url, alt, focal_x, focal_y, zoom }
 *
 * Legacy top-level content fields are never overwritten — only read,
 * once, as a fallback on initial load:
 *   title    = macro_sensory.title    ?? content.headline
 *   subtitle = macro_sensory.subtitle ?? content.lead
 *   caption  = macro_sensory.caption  ?? content.caption
 * There is deliberately NO content.body → text_blocks fallback — the
 * real Reader does not do that. If text_blocks are absent, exactly one
 * empty local block is created so the author always has somewhere to
 * type; nothing is mutated on the backend before an explicit Save.
 *
 * ── CROP CONTRACT (confirmed, critical) ──────────────────────────────
 * Without an explicit crop, the Reader shows the image at its own
 * natural ratio, centered, not stretched to the container width
 * (h-auto w-auto max-w-full inside a flex/justify-center wrapper),
 * capped by maxHeight (Desktop 520, Mobile 300). With an explicit
 * crop, the Reader forces a 5:4 frame with cover/focal/zoom via
 * editorialImageStyle. hasExplicitEditorialCrop(image) — the real
 * helper, not a local heuristic — decides which mode applies, both in
 * the authoring crop modal's aspect and in this file's own local
 * preview (SensoryImage below). EditorialCropV2 itself is used with
 * aspectRatio="5/4" (never 4:5) for both main and mobile images.
 *
 * resolveMediaUrl(urlString) — confirmed to take a URL STRING, not a
 * media object — is used only at render time, on the already-stored
 * image.url, everywhere an image is actually displayed (PhotoField's
 * management thumbnail, both SensoryImage branches, and the crop
 * modal's url prop). It is never used when resolving a freshly
 * picked/uploaded media object — mediaRecordUrl(media) plus the
 * file_url/url fallback chain handles that instead — and its result is
 * never written back into saved content.
 *
 * ── PREVIEW ────────────────────────────────────────────────────────────
 * Fixed Reader colors/background (#EFE6D5 background; eyebrow/caption
 * #8A6A46 and #5B4326 respectively; title #2A1D14; subtitle #5B4326
 * italic; paragraphs #2A1D14; accent phrase #3E2E20 italic with a
 * #A67B4A left border) — MACRO_SENSORY has no palette/background
 * settings, same as MACRO. Desktop: 56/44 photo+caption-left /
 * text-right grid. Mobile: eyebrow → title → subtitle → photo →
 * caption → paragraphs → sensory notes → accent phrase, mobile image
 * if present else main image. Sensory notes render as a confirmed
 * label/text two-column list (1fr/2fr via a 3-col grid) with subtle
 * row dividers — deliberately NOT rendered as cards. A MACRO_SENSORY
 * with no active image renders the confirmed text-only mode (single
 * column, max-width 760, no broken image placeholder) instead of an
 * empty photo box.
 *
 * Provider calls = 0. AI cost = $0.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle, hasExplicitEditorialCrop } from "@/lib/editorialReaderLayout.mjs";
import { MediaLibraryPickerV2, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

const CROP_ASPECT = "5/4";
const MAIN_FRAME_LABEL = "Основное фото · кадр 5:4";
const MOBILE_FRAME_LABEL = "Мобильное фото · кадр 5:4";
const MAX_SENSORY_NOTES = 6;

const DESKTOP_MAX_HEIGHT = 520;
const MOBILE_MAX_HEIGHT = 300;

// Confirmed Reader colors — fixed, no palette/background settings.
const BG = "#EFE6D5";
const EYEBROW_COLOR = "#8A6A46";
const TITLE_COLOR = "#2A1D14";
const SUBTITLE_COLOR = "#5B4326";
const PARAGRAPH_COLOR = "#2A1D14";
const ACCENT_TEXT_COLOR = "#3E2E20";
const ACCENT_BORDER_COLOR = "#A67B4A";
const CAPTION_COLOR = "#5B4326";

function normalizeImage(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

let idSeq = 0;
function newId(prefix) {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
}

// Reader-compatible fallback on initial load — see file header.
// Deliberately no content.body fallback for text_blocks.
function deriveInitial(content) {
  const c = content || {};
  const ms = c.macro_sensory || {};
  return {
    eyebrow: ms.eyebrow || "",
    title: ms.title ?? c.headline ?? "",
    subtitle: ms.subtitle ?? c.lead ?? "",
    textBlocks: Array.isArray(ms.text_blocks) && ms.text_blocks.length
      ? ms.text_blocks.map((b, i) => ({ id: b?.id || `tb-legacy-${i}`, text: String(b?.text || "") }))
      : [{ id: newId("tb"), text: "" }],
    sensoryNotes: Array.isArray(ms.sensory_notes)
      ? ms.sensory_notes.slice(0, MAX_SENSORY_NOTES).map((n, i) => ({
        id: n?.id || `sn-legacy-${i}`, label: n?.label || "", text: n?.text || "",
      }))
      : [],
    accentPhrase: ms.accent_phrase || "",
    caption: ms.caption ?? c.caption ?? "",
    mainImage: normalizeImage(ms.main_image),
    mobileImage: normalizeImage(ms.mobile_image),
  };
}

// Mirrors the real Reader's image contract exactly (§9): explicit crop
// → forced 5:4 cover/focal/zoom via editorialImageStyle; no explicit
// crop → natural ratio, capped by maxHeight, no forced frame. Publication
// images never get rounded corners. The stored image.url is only
// resolved through resolveMediaUrl(urlString) at render time here —
// never written back into saved content.
function SensoryImage({ image, maxHeight }) {
  if (!image?.url) return null;
  const resolvedUrl = resolveMediaUrl(image.url);
  const hasCrop = hasExplicitEditorialCrop(image);
  if (hasCrop) {
    return (
      <div className="relative overflow-hidden bg-espresso/10" style={{ aspectRatio: CROP_ASPECT }}>
        <img src={resolvedUrl} alt={image.alt || ""} className="h-full w-full" style={editorialImageStyle(image)} />
      </div>
    );
  }
  return (
    <div className="flex justify-center">
      <img src={resolvedUrl} alt={image.alt || ""} className="block h-auto w-auto max-w-full"
        style={{ maxHeight: `${maxHeight}px` }} />
    </div>
  );
}

function SensoryNotesPreview({ notes }) {
  const filled = notes.filter((n) => n.label || n.text);
  if (!filled.length) return null;
  return (
    <div className="mt-4">
      <div className="text-[10px] uppercase tracking-widest" style={{ color: EYEBROW_COLOR }}>Сенсорные ноты</div>
      <div className="mt-2 divide-y divide-espresso/10">
        {filled.map((n) => (
          <div key={n.id} className="grid grid-cols-3 gap-3 py-2">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: EYEBROW_COLOR }}>{n.label}</div>
            <div className="col-span-2 font-serif text-sm" style={{ color: TITLE_COLOR }}>{n.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SensoryTextBlock({ eyebrow, title, subtitle, textBlocks, sensoryNotes, accentPhrase, centered }) {
  const paragraphs = textBlocks.map((b) => b.text).filter(Boolean);
  return (
    <div className={centered ? "text-center" : ""}>
      {eyebrow && <div className="text-[10px] uppercase tracking-widest" style={{ color: EYEBROW_COLOR }}>{eyebrow}</div>}
      {title && <div className="mt-1 font-serif text-xl leading-snug" style={{ color: TITLE_COLOR }}>{title}</div>}
      {subtitle && <div className="mt-2 font-serif text-sm italic" style={{ color: SUBTITLE_COLOR }}>{subtitle}</div>}
      {paragraphs.length > 0 && (
        <div className="mt-3 space-y-2">
          {paragraphs.map((t, i) => (
            <p key={i} className="text-sm leading-relaxed" style={{ color: PARAGRAPH_COLOR }}>{t}</p>
          ))}
        </div>
      )}
      <SensoryNotesPreview notes={sensoryNotes} />
      {accentPhrase && (
        <div className="mt-4 border-l-2 pl-3 font-serif text-sm italic"
          style={{ color: ACCENT_TEXT_COLOR, borderColor: ACCENT_BORDER_COLOR }}>
          {accentPhrase}
        </div>
      )}
    </div>
  );
}

function PhotoField({ label, helper, image, onUpload, onLibrary, onCrop, onRemove, onAltChange, uploading, testidPrefix }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] text-espresso/65">{label}</label>
      {helper && <p className="mb-2 text-xs text-espresso/50">{helper}</p>}
      {image?.url ? (
        <div className="flex items-start gap-3">
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
            <img src={resolveMediaUrl(image.url)} alt={image.alt || ""} className="h-full w-full object-cover"
              style={{ objectPosition: `${image.focal_x * 100}% ${image.focal_y * 100}%` }} />
          </div>
          <div className="flex-1 space-y-1.5">
            <input value={image.alt} onChange={(e) => onAltChange(e.target.value)}
              placeholder="Описание изображения (alt)"
              className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
              data-testid={`${testidPrefix}-alt`} />
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={onUpload} disabled={uploading}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50"
                data-testid={`${testidPrefix}-replace`}>
                {uploading ? "Загрузка…" : "Заменить фото"}
              </button>
              <button type="button" onClick={onLibrary}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                data-testid={`${testidPrefix}-library`}>
                Из Медиатеки
              </button>
              <button type="button" onClick={onCrop}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                data-testid={`${testidPrefix}-crop`}>
                Настроить кадр
              </button>
              <button type="button" onClick={onRemove}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70"
                data-testid={`${testidPrefix}-remove`}>
                Удалить фото
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button type="button" onClick={onUpload} disabled={uploading}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
            data-testid={`${testidPrefix}-upload`}>
            {uploading ? "Загрузка…" : "Загрузить фото"}
          </button>
          <button type="button" onClick={onLibrary}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
            data-testid={`${testidPrefix}-library-empty`}>
            Из Медиатеки
          </button>
        </div>
      )}
    </div>
  );
}

export function EditorialMacroSensoryV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitial(c);

  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [title, setTitle] = useState(initial.title);
  const [subtitle, setSubtitle] = useState(initial.subtitle);
  const [textBlocks, setTextBlocks] = useState(initial.textBlocks);
  const [sensoryNotes, setSensoryNotes] = useState(initial.sensoryNotes);
  const [accentPhrase, setAccentPhrase] = useState(initial.accentPhrase);
  const [caption, setCaption] = useState(initial.caption);
  const [mainImage, setMainImage] = useState(initial.mainImage);
  const [mobileImage, setMobileImage] = useState(initial.mobileImage);

  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [librarySlot, setLibrarySlot] = useState(null); // null | "main" | "mobile"
  const [cropSlot, setCropSlot] = useState(null); // null | "main" | "mobile"
  const mainFileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");

  useEffect(() => {
    const next = deriveInitial(block.content || {});
    setEyebrow(next.eyebrow);
    setTitle(next.title);
    setSubtitle(next.subtitle);
    setTextBlocks(next.textBlocks);
    setSensoryNotes(next.sensoryNotes);
    setAccentPhrase(next.accentPhrase);
    setCaption(next.caption);
    setMainImage(next.mainImage);
    setMobileImage(next.mobileImage);
    setSaveState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);  const mobileActiveImage = mobileImage?.url ? mobileImage : mainImage;

  // ── Text blocks ──────────────────────────────────────────────────
  const addTextBlock = () => setTextBlocks((blocks) => [...blocks, { id: newId("tb"), text: "" }]);
  const updateTextBlock = (index, value) => {
    setTextBlocks((blocks) => blocks.map((b, i) => (i === index ? { ...b, text: value } : b)));
  };
  const removeTextBlock = (index) => {
    setTextBlocks((blocks) => (blocks.length > 1 ? blocks.filter((_, i) => i !== index) : blocks));
  };
  const moveTextBlock = (index, dir) => {
    setTextBlocks((blocks) => {
      const to = index + dir;
      if (to < 0 || to >= blocks.length) return blocks;
      const next = [...blocks];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  // ── Sensory notes ────────────────────────────────────────────────
  const addSensoryNote = () => {
    if (sensoryNotes.length >= MAX_SENSORY_NOTES) return;
    setSensoryNotes((notes) => [...notes, { id: newId("sn"), label: "", text: "" }]);
  };
  const updateSensoryNote = (index, field, value) => {
    setSensoryNotes((notes) => notes.map((n, i) => (i === index ? { ...n, [field]: value } : n)));
  };
  const removeSensoryNote = (index) => setSensoryNotes((notes) => notes.filter((_, i) => i !== index));
  const moveSensoryNote = (index, dir) => {
    setSensoryNotes((notes) => {
      const to = index + dir;
      if (to < 0 || to >= notes.length) return notes;
      const next = [...notes];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  // ── Media ────────────────────────────────────────────────────────
  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    const img = normalizeImage({ media_id: media.id, url });
    if (librarySlot === "mobile") setMobileImage(img);
    else setMainImage(img);
    setLibrarySlot(null);
  };

  const uploadTo = async (file, slot) => {
    const setUploading = slot === "mobile" ? setUploadingMobile : setUploadingMain;
    const setImage = slot === "mobile" ? setMobileImage : setMainImage;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setImage(normalizeImage({ media_id: data.id, url }));
      toast.success("Фото загружено");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleMainFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadTo(file, "main");
  };
  const handleMobileFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadTo(file, "mobile");
  };

  const saveCrop = (values) => {
    if (cropSlot === "mobile") setMobileImage((img) => (img ? { ...img, ...values } : img));
    else if (cropSlot === "main") setMainImage((img) => (img ? { ...img, ...values } : img));
    setCropSlot(null);
  };

  // ── Save ─────────────────────────────────────────────────────────
  const save = async () => {
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        macro_sensory: {
          eyebrow,
          title,
          subtitle,
          text_blocks: textBlocks.map((b) => ({ id: b.id, text: b.text })),
          sensory_notes: sensoryNotes.map((n) => ({ id: n.id, label: n.label, text: n.text })),
          accent_phrase: accentPhrase,
          caption,
          main_image: mainImage ? { ...mainImage } : null,
          mobile_image: mobileImage ? { ...mobileImage } : null,
        },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      const savedMediaItems = [mainImage, mobileImage]
        .filter((img) => img?.media_id && img?.url)
        .map((img) => ({ id: img.media_id, url: img.url }));
      onSaved?.({ ...block, content: nextContent }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  const workspaceTitle = title || "Макро · сенсорные ноты";
  const cropImage = cropSlot === "mobile" ? mobileImage : cropSlot === "main" ? mainImage : null;
  const cropFrameLabel = cropSlot === "mobile" ? MOBILE_FRAME_LABEL : MAIN_FRAME_LABEL;

  return (
    <div
      onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-macrosensory-workspace"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Макро · сенсорные ноты</div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{workspaceTitle}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-macrosensory-save-state">
              {saveLabel}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          {/* Main photo */}
          <PhotoField
            label="Основное фото"
            image={mainImage}
            uploading={uploadingMain}
            onUpload={() => mainFileInputRef.current?.click()}
            onLibrary={() => setLibrarySlot("main")}
            onCrop={() => setCropSlot("main")}
            onRemove={() => setMainImage(null)}
            onAltChange={(v) => setMainImage((img) => (img ? { ...img, alt: v } : img))}
            testidPrefix="editorial-v2-macrosensory-main"
          />
          <input ref={mainFileInputRef} type="file" accept="image/*" className="hidden"
            onChange={handleMainFile} data-testid="editorial-v2-macrosensory-main-file-input" />

          {/* Mobile photo */}
          <PhotoField
            label="Мобильное фото · необязательно"
            helper="Если не добавлено, на телефоне используется основное фото."
            image={mobileImage}
            uploading={uploadingMobile}
            onUpload={() => mobileFileInputRef.current?.click()}
            onLibrary={() => setLibrarySlot("mobile")}
            onCrop={() => setCropSlot("mobile")}
            onRemove={() => setMobileImage(null)}
            onAltChange={(v) => setMobileImage((img) => (img ? { ...img, alt: v } : img))}
            testidPrefix="editorial-v2-macrosensory-mobile"
          />
          <input ref={mobileFileInputRef} type="file" accept="image/*" className="hidden"
            onChange={handleMobileFile} data-testid="editorial-v2-macrosensory-mobile-file-input" />

          {/* Text */}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Метка раздела</label>
              <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macrosensory-eyebrow" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Заголовок</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macrosensory-title" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Подзаголовок / лид</label>
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macrosensory-subtitle" />
            </div>

            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Абзацы</label>
              <div className="space-y-2">
                {textBlocks.map((b, i) => (
                  <div key={b.id} className="flex gap-2">
                    <textarea value={b.text} onChange={(e) => updateTextBlock(i, e.target.value)} rows={3}
                      className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                      data-testid={`editorial-v2-macrosensory-block-${i}`} />
                    <div className="flex shrink-0 flex-col gap-1">
                      <button type="button" onClick={() => moveTextBlock(i, -1)} disabled={i === 0}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                        data-testid={`editorial-v2-macrosensory-block-up-${i}`}>
                        ▲
                      </button>
                      <button type="button" onClick={() => moveTextBlock(i, 1)} disabled={i === textBlocks.length - 1}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                        data-testid={`editorial-v2-macrosensory-block-down-${i}`}>
                        ▼
                      </button>
                      <button type="button" onClick={() => removeTextBlock(i)} disabled={textBlocks.length <= 1}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70 disabled:opacity-30"
                        data-testid={`editorial-v2-macrosensory-block-remove-${i}`}>
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addTextBlock}
                className="mt-2 text-xs text-espresso/60 underline underline-offset-2"
                data-testid="editorial-v2-macrosensory-block-add">
                + Добавить абзац
              </button>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="block text-[11px] text-espresso/60">
                  Сенсорные ноты {sensoryNotes.length > 0 && `(${sensoryNotes.length} из ${MAX_SENSORY_NOTES})`}
                </label>
              </div>
              <div className="space-y-2">
                {sensoryNotes.map((n, i) => (
                  <div key={n.id} className="flex gap-2 rounded-lg border border-espresso/15 p-2">
                    <div className="flex-1 space-y-1.5">
                      <input value={n.label} onChange={(e) => updateSensoryNote(i, "label", e.target.value)}
                        placeholder="Метка (например, аромат)"
                        className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
                        data-testid={`editorial-v2-macrosensory-note-label-${i}`} />
                      <textarea value={n.text} onChange={(e) => updateSensoryNote(i, "text", e.target.value)} rows={2}
                        placeholder="Описание"
                        className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
                        data-testid={`editorial-v2-macrosensory-note-text-${i}`} />
                    </div>
                    <div className="flex shrink-0 flex-col gap-1">
                      <button type="button" onClick={() => moveSensoryNote(i, -1)} disabled={i === 0}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                        data-testid={`editorial-v2-macrosensory-note-up-${i}`}>
                        ▲
                      </button>
                      <button type="button" onClick={() => moveSensoryNote(i, 1)} disabled={i === sensoryNotes.length - 1}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-30"
                        data-testid={`editorial-v2-macrosensory-note-down-${i}`}>
                        ▼
                      </button>
                      <button type="button" onClick={() => removeSensoryNote(i)}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70"
                        data-testid={`editorial-v2-macrosensory-note-remove-${i}`}>
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {sensoryNotes.length < MAX_SENSORY_NOTES ? (
                <button type="button" onClick={addSensoryNote}
                  className="mt-2 text-xs text-espresso/60 underline underline-offset-2"
                  data-testid="editorial-v2-macrosensory-note-add">
                  + Добавить ноту
                </button>
              ) : (
                <p className="mt-2 text-xs text-espresso/50">Можно добавить до {MAX_SENSORY_NOTES} нот.</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Акцентная фраза</label>
              <textarea value={accentPhrase} onChange={(e) => setAccentPhrase(e.target.value)} rows={2}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macrosensory-accent" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Подпись к фотографии</label>
              <input value={caption} onChange={(e) => setCaption(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-macrosensory-caption" />
            </div>
          </div>

          {/* Local preview — mirrors the confirmed MacroSensoryMaster Reader contract */}
          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4" data-testid="editorial-v2-macrosensory-preview">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "desktop" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-macrosensory-preview-desktop">
                    Desktop
                  </button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "mobile" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-macrosensory-preview-mobile">
                    Mobile
                  </button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                mainImage?.url ? (
                  <div className="mx-auto" style={{ maxWidth: "1120px", backgroundColor: BG }}>
                    <div className="p-4">
                      <div className="grid gap-2.5 lg:gap-3" style={{ gridTemplateColumns: "minmax(0,56fr) minmax(0,44fr)" }}>
                        <div>
                          <SensoryImage image={mainImage} maxHeight={DESKTOP_MAX_HEIGHT} />
                          {caption && (
                            <div className="mt-2 text-[11px] italic" style={{ color: CAPTION_COLOR }}>{caption}</div>
                          )}
                        </div>
                        <SensoryTextBlock eyebrow={eyebrow} title={title} subtitle={subtitle}
                          textBlocks={textBlocks} sensoryNotes={sensoryNotes} accentPhrase={accentPhrase} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto" style={{ maxWidth: "760px", backgroundColor: BG }}>
                    <div className="px-6 py-10">
                      <SensoryTextBlock eyebrow={eyebrow} title={title} subtitle={subtitle}
                        textBlocks={textBlocks} sensoryNotes={sensoryNotes} accentPhrase={accentPhrase} />
                    </div>
                  </div>
                )
              ) : (
                <div className="mx-auto max-w-[240px]" style={{ backgroundColor: BG }}>
                  <div className="p-3">
                    {(eyebrow || title || subtitle) && (
                      <div className="mb-3">
                        {eyebrow && <div className="text-[10px] uppercase tracking-widest" style={{ color: EYEBROW_COLOR }}>{eyebrow}</div>}
                        {title && <div className="mt-1 font-serif text-lg leading-snug" style={{ color: TITLE_COLOR }}>{title}</div>}
                        {subtitle && <div className="mt-1 font-serif text-sm italic" style={{ color: SUBTITLE_COLOR }}>{subtitle}</div>}
                      </div>
                    )}
                    {mobileActiveImage?.url && (
                      <>
                        <SensoryImage image={mobileActiveImage} maxHeight={MOBILE_MAX_HEIGHT} />
                        {caption && (
                          <div className="mt-2 text-[11px] italic" style={{ color: CAPTION_COLOR }}>{caption}</div>
                        )}
                      </>
                    )}
                    {(() => {
                      const paragraphs = textBlocks.map((b) => b.text).filter(Boolean);
                      if (!paragraphs.length) return null;
                      return (
                        <div className="mt-3 space-y-2">
                          {paragraphs.map((t, i) => (
                            <p key={i} className="text-sm leading-relaxed" style={{ color: PARAGRAPH_COLOR }}>{t}</p>
                          ))}
                        </div>
                      );
                    })()}
                    <SensoryNotesPreview notes={sensoryNotes} />
                    {accentPhrase && (
                      <div className="mt-4 border-l-2 pl-3 font-serif text-sm italic"
                        style={{ color: ACCENT_TEXT_COLOR, borderColor: ACCENT_BORDER_COLOR }}>
                        {accentPhrase}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-macrosensory-close">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-macrosensory-preview-toggle">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving"}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50"
            data-testid="editorial-v2-macrosensory-save">
            Сохранить изменения
          </button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={librarySlot !== null} onClose={() => setLibrarySlot(null)} onPick={pickFromLibrary} />

      <EditorialCropV2
        open={cropSlot !== null}
        url={resolveMediaUrl(cropImage?.url || "")}
        aspectRatio={CROP_ASPECT}
        frameLabel={cropFrameLabel}
        focalX={cropImage?.focal_x ?? 0.5}
        focalY={cropImage?.focal_y ?? 0.5}
        zoom={cropImage?.zoom ?? 1}
        onSave={saveCrop}
        onClose={() => setCropSlot(null)}
      />
    </div>
  );
}